import { Component, OnDestroy, OnInit } from '@angular/core';
import { Edit, LucideAngularModule, Save, Trash } from 'lucide-angular';
import { ActivatedRoute, Router } from '@angular/router';
import { FormArray, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { AbstractEntityEditComponent } from '@app/shared/abstract/abstract-entity-edit.component';
import { ButtonComponent } from '@app/partials/form/button/button.component';
import { TextInputComponent } from '@app/partials/form/text-input/text-input.component';
import { UserModel } from '@app/users/shared/user.model';
import { ContactService } from '@app/contacts/shared/contact.service';
import { ArrayInputComponent } from '@app/partials/form/array-input/array-input.component';
import { ModalComponent } from '@app/partials/modal/modal.component';
import { Editor, NgxEditorModule, Toolbar } from 'ngx-editor';
import { EventService } from '@app/events/shared/event.service';
import { RouteParams } from '@app/shared/abstract/route-params';
import { map, Observable } from 'rxjs';
import { SelectComponent } from '@app/partials/form/select/select.component';
import { DatePipe } from '@angular/common';

@Component({
  selector: 'app-edit',
  imports: [
    ButtonComponent,
    ReactiveFormsModule,
    TextInputComponent,
    ArrayInputComponent,
    ModalComponent,
    LucideAngularModule,
    NgxEditorModule,
    SelectComponent,
    DatePipe,
  ],
  templateUrl: './contact-edit.component.html',
})
export class ContactEditComponent extends AbstractEntityEditComponent implements OnInit, OnDestroy {
  protected processing = false;
  protected validationErrors: string[] = [];
  protected user: UserModel;
  protected readonly SaveIcon = Save;
  protected readonly TrashIcon = Trash;
  protected readonly EditIcon = Edit;

  protected noteForm!: FormGroup<{
    note: FormControl<string | null>;
  }>;

  protected processingNote = false;
  protected showNoteForm = false;
  protected editNoteId: number | null = null;

  protected isDeleteModalOpen = false;
  protected deleteNotesId: number | null = null;

  // protected eventsChoices$: Observable<{ label: string; value: number }[]>;
  protected contactStatusChoices = [
    { label: 'Opportunity', value: 'Opportunity' },
    { label: 'Pencil Booking', value: 'Pencil Booking' },
    { label: 'Confirmed', value: 'Confirmed' },
    { label: 'Cancelled', value: 'Cancelled' },
    { label: 'Closed', value: 'Closed' },
  ];

  editor?: Editor;
  toolbar: Toolbar = [
    ['bold', 'italic'],
    ['underline', 'strike'],
    ['ordered_list', 'bullet_list', 'link'],
    ['text_color', 'background_color'],
    [{ heading: ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'] }],
    ['align_left', 'align_center', 'align_right', 'align_justify'],
    ['undo', 'redo'],
  ];

  form = new FormGroup({
    name: new FormControl('', [Validators.required, Validators.minLength(3), Validators.maxLength(50)]),
    email: new FormControl('', [Validators.required]),
    second_email: new FormControl('', [Validators.maxLength(255)]),
    password: new FormControl('', [Validators.required, Validators.minLength(6), Validators.maxLength(50)]),
    address: new FormControl('', [Validators.maxLength(255)]), // Kept for backward compatibility
    address_line_1: new FormControl('', [Validators.required, Validators.maxLength(255)]),
    address_line_2: new FormControl<string | null>(null, [Validators.maxLength(255)]),
    city: new FormControl('', [Validators.required, Validators.maxLength(100)]),
    postcode: new FormControl('', [Validators.required, Validators.maxLength(20)]),
    country: new FormControl('', [Validators.required, Validators.maxLength(100)]),
    phone_numbers: new FormArray<FormControl<string>>([]),
    // event_ids: new FormControl<number[]>([]),
    contact_status: new FormControl<'Opportunity' | 'Pencil Booking' | 'Confirmed' | 'Cancelled' | 'Closed'>(
      'Opportunity',
      [Validators.required],
    ),
  });

  constructor(
    private contactsService: ContactService,
    private route: ActivatedRoute,
    // private eventsService: EventService,
    private router: Router,
  ) {
    super();

    // this.eventsChoices$ = this.eventsService
    //   .getPage(
    //     new RouteParams({
    //       pageNum: 1,
    //       pageSize: 1000,
    //     }),
    //   )
    //   .pipe(
    //     map((res) => res.data),
    //     map((events) =>
    //       events.map((event) => ({
    //         value: event.id,
    //         label: event.name,
    //       })),
    //     ),
    //   );

    if (this.route.snapshot.data['user']) {
      this.user = this.route.snapshot.data['user'];
      if (!this.user.notes) {
        this.user.notes = [];
      }
      // this.form.get('event_ids')?.setValue(this.user.event_ids || []);
      this.form.patchValue(this.user);
      if (this.user.phone_numbers?.length) {
        this.user.phone_numbers.forEach((phone) => {
          (this.form.get('phone_numbers') as FormArray).push(new FormControl(phone));
        });
      }
      this.form.get('password')?.clearValidators();
    } else {
      this.user = { notes: [] } as unknown as UserModel;
      this.form.patchValue(this.user);
    }
  }

  ngOnInit(): void {
    this.editor = new Editor({
      history: true,
      keyboardShortcuts: true,
    });

    this.noteForm = new FormGroup({
      note: new FormControl('', {
        validators: [Validators.required, Validators.maxLength(1000)],
      }),
    });
  }

  ngOnDestroy(): void {
    this.editor?.destroy();
  }

  save() {
    if (this.form.invalid) {
      return;
    }
    this.processing = true;

    const data = {
      id: this.user.id,
      name: this.form.get('name')?.value,
      email: this.form.get('email')?.value,
      second_email: this.form.get('second_email')?.value,
      address: this.form.get('address')?.value, // Kept for backward compatibility
      address_line_1: this.form.get('address_line_1')?.value,
      address_line_2: this.form.get('address_line_2')?.value,
      city: this.form.get('city')?.value,
      postcode: this.form.get('postcode')?.value,
      country: this.form.get('country')?.value,
      phone_numbers: this.form.get('phone_numbers')?.value,
      password: this.form.get('password')?.value || null,
      // event_ids: this.form.get('event_ids')?.value || [],
      contact_status: this.form.get('contact_status')?.value,
    };

    this.contactsService.save(data as unknown as UserModel).subscribe({
      next: (res) => {
        this.processing = false;
        this.user = res.data;
        if (!this.user.notes) {
          this.user.notes = [];
        }
        this.form.patchValue(this.user);
        this.router.navigate(['/contacts/' + this.user.id + '/edit']);
        this.form.markAsPristine();
      },
      error: (res) => {
        if (res.status === 422) {
          AbstractEntityEditComponent.bindErrorsToForm(res.error, this.form);
        }
        this.processing = false;
      },
    });
  }

  updateNote(noteId: number) {
    this.editNoteId = noteId;
    this.showNoteForm = true;
    const note = this.user.notes.find((note) => note.id === noteId);
    if (note) {
      this.noteForm.patchValue({
        note: note.note,
      });
    }
  }

  saveNote() {
    if (!this.noteForm) {
      return;
    }
    if (this.noteForm.invalid) {
      return;
    }

    const data = this.noteForm.value as any;
    data.type = 'internal';
    data.noteable_type = 'user';
    data.noteable_id = this.user.id;

    this.processingNote = true;

    // If editNoteId exists, update existing note
    if (this.editNoteId) {
      this.contactsService.updateContactNote(data, this.editNoteId).subscribe({
        next: (response) => {
          // Update the note in the array
          const index = this.user.notes.findIndex((note) => note.id === this.editNoteId);
          if (index !== -1) {
            this.user.notes[index] = response.data;
          }
          this.cancelNote();
          this.processingNote = false;
        },
        error: (res) => {
          if (res.status === 422) {
            AbstractEntityEditComponent.bindErrorsToForm(res.error, this.noteForm);
          }
          this.processingNote = false;
        },
      });
    } else {
      // Create new note
      this.contactsService.createContactNote(data).subscribe({
        next: (response) => {
          if (!this.user.notes) {
            this.user.notes = [];
          }
          this.user.notes.push(response.data);
          this.cancelNote();
          this.processingNote = false;
        },
        error: (res) => {
          if (res.status === 422) {
            AbstractEntityEditComponent.bindErrorsToForm(res.error, this.noteForm);
          }
          this.processingNote = false;
        },
      });
    }
  }

  cancelNote() {
    this.noteForm?.reset();
    this.showNoteForm = false;
    this.editNoteId = null;
  }

  deleteNote(id: any) {
    this.isDeleteModalOpen = true;
    this.deleteNotesId = id;
  }

  confirmDelete() {
    if (this.deleteNotesId) {
      this.contactsService.deleteContactNote(this.deleteNotesId).subscribe({
        next: () => {
          this.user.notes = this.user.notes.filter((note) => note.id !== this.deleteNotesId);
          this.isDeleteModalOpen = false;
          this.deleteNotesId = null;
        },
        error: (res) => {
          console.error('Error deleting note:', res);
        },
      });
    }
  }

  cancel() {
    return this.router.navigate(['/contacts']);
  }
}
